// src/components/DemonCard.js — v3
import React from "react";
import { STAT_META, getWeeklyHoroscope, computeLevel, LEVEL_THRESHOLDS, ONI_IMG_VERSION } from "../data/demons";
import "./DemonCard.css";

const PERK_LABELS = {
  illegal_serve:"Service Illégal",cheater:"Tricheur Assumé",lucky_edge:"Bord de Table & Filet Chanceux",
  no_backend:"Revers Inexistant",sweater_guy:"Survêtement Vintage",tennis_guy:"Ex-Tennisman",
  slapper:"Frappeur Sec",excuse_guy:"Roi des Excuses",equipment_junky:"Accro au Matériel",
  diving_guy:"Plongeur",too_many_man:"Trop de Tics",ball_breaker:"Casseur de Balles",
  screamer:"Crieur",rager:"Soupe au Lait",high_toss:"Service Lancé Haut",perfect_no_tac:"Technique Parfaite, Tactique Zéro",
  trick_shot:"Coup Fantaisie",old_monkey:"Vieilles Combines de Vétéran",defender:"Défenseur",
  attacker:"Attaquant",spinner:"Effeur",looper:"Lifteur",fisher:"Pêcheur",
};

function StatBar({ statKey, value }) {
  const meta = STAT_META[statKey];
  if (!meta) return null;
  return (
    <div className="dc-stat-row">
      <span className="dc-stat-label">{meta.label}</span>
      <div className="dc-stat-track">
        <div className="dc-stat-fill" style={{ width: value + "%", background: meta.color }} />
      </div>
      <span className="dc-stat-val" style={{ color: meta.color }}>{value}</span>
    </div>
  );
}

function OrderChaosAxis({ stats }) {
  const orderScore = Math.round((stats.control + stats.patience + stats.discipline) / 3);
  const chaosScore = Math.round((stats.chaos + stats.risk + stats.creativity) / 3);
  const pos = Math.round((chaosScore / (orderScore + chaosScore)) * 100);
  return (
    <div className="dc-axis">
      <div className="dc-axis-labels">
        <span>⚖ ORDRE</span>
        <span>CHAOS ☠</span>
      </div>
      <div className="dc-axis-track">
        <div className="dc-axis-fill" style={{ width: pos + "%" }} />
        <div className="dc-axis-pip" style={{ left: `calc(${pos}% - 4px)` }} />
      </div>
      <div className="dc-axis-vals">
        <span style={{color:"var(--blue-oni)"}}>{orderScore}</span>
        <span style={{color:"var(--blood)"}}>{chaosScore}</span>
      </div>
    </div>
  );
}

export default function DemonCard({ demon, compact = false, showHoroscope = false, xp = 0 }) {
  const { oni, stats, perks } = demon;
  const horoscope = showHoroscope ? getWeeklyHoroscope(oni.id) : null;
  const levelInfo = computeLevel(xp);

  // Top 5 stats
  const topStats = Object.entries(stats)
    .sort(([,a],[,b]) => b - a)
    .slice(0, compact ? 3 : 6);

  return (
    <div className={`demon-card${compact ? " compact" : ""}`}>
      <div className="demon-card-glow" />

      {/* Header */}
      <div className="dc-header">
        <div className="dc-avatar-wrap">
          {oni.image ? (
            <div className="dc-avatar-img-wrap">
              <img
                src={`${oni.image}?v=${ONI_IMG_VERSION}`}
                alt={oni.name}
                className="dc-avatar-img"
                onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}
              />
              <div className="dc-avatar dc-avatar-fallback" style={{display:"none"}}>{oni.emoji}</div>
            </div>
          ) : (
            <div className="dc-avatar">{oni.emoji}</div>
          )}
          {!compact && (
            <div className="dc-sign" title={`Signe : ${oni.signName}`}>
              {oni.sign}
            </div>
          )}
        </div>
        <div className="dc-identity">
          <span className="dc-jp">{oni.jp}</span>
          <h2 className="dc-en">{oni.name.toUpperCase()}</h2>
          <p className="dc-tagline">{oni.tagline}</p>
          <div style={{display:"flex", gap:"6px", flexWrap:"wrap", marginTop:"0.5rem"}}>
            <span className="badge badge-blood">{oni.name}</span>
            <span className="badge badge-ghost">{oni.sign} {oni.signName}</span>
          </div>
        </div>
        {/* Level */}
        <div className="dc-level">
          <div className="dc-level-badge">{levelInfo.current.badge}</div>
          <div className="dc-level-info">
            <span className="dc-level-num">LV.{levelInfo.current.level}</span>
            <span className="dc-level-title">{levelInfo.current.title}</span>
          </div>
        </div>
      </div>

      {/* Level progress */}
      {!compact && levelInfo.next && (
        <div className="dc-level-bar">
          <div className="dc-level-bar-fill" style={{width: levelInfo.progress + "%"}} />
          <span className="dc-level-xp">{xp} XP → {levelInfo.next.xp} XP pour LV.{levelInfo.next.level}</span>
        </div>
      )}

      {/* Description */}
      {!compact && <p className="dc-desc">{oni.desc}</p>}

      {/* Ordre / Chaos axis */}
      <OrderChaosAxis stats={stats} />

      {/* Stats */}
      <div className="dc-stats">
        {topStats.map(([key, val]) => (
          <StatBar key={key} statKey={key} value={val} />
        ))}
      </div>

      {/* Perks */}
      {perks?.length > 0 && !compact && (
        <div className="dc-perks">
          <p className="dc-block-title">PERKS DÉMONIAQUES</p>
          <div className="dc-perks-tags">
            {perks.map(p => (
              <span key={p} className="dc-perk-tag">⚡ {PERK_LABELS[p] || p}</span>
            ))}
          </div>
        </div>
      )}

      {/* Horoscope pongistique */}
      {showHoroscope && horoscope && (
        <div className="dc-horoscope">
          <p className="dc-block-title">◈ HOROSCOPE PONGISTIQUE · SEMAINE EN COURS</p>
          <div className="dc-horoscope-grid">
            {horoscope.map((h, i) => (
              <div key={i} className="dc-horo-card">
                <div className="dc-horo-header">
                  <span className="dc-horo-intensity">{h.intensity}</span>
                  <span className="dc-horo-domain">{h.domain}</span>
                </div>
                <p className="dc-horo-text">{h.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
